/**
 * <bundle-engine>
 *
 * Wraps a grid of .gv-bundle-card__button elements (rendered by
 * sections/bundle-pricing.liquid via snippets/gv-bundle-card.liquid). Each
 * button independently adds its tier's variant/quantity to the cart — there
 * is no shared selection state like <offer-selector> (assets/gv-offers.js),
 * since bundle tiers are independent purchases, not mutually exclusive
 * choices.
 *
 * Deliberately mirrors the existing cart/add.js POST pattern already used
 * by ProductForm and FreeShippingBar in theme.js (same request shape, same
 * cart:prepare-bundled-sections / variant:add / cart:change events), so the
 * existing cart drawer, cart count, and free-shipping bar all update
 * automatically with zero changes to any of that code.
 */
if (!window.customElements.get('bundle-engine')) {
	class BundleEngine extends HTMLElement {
		connectedCallback() {
			this._onClickListener = this._onClick.bind(this)
			this.addEventListener('click', this._onClickListener)
		}

		disconnectedCallback() {
			this.removeEventListener('click', this._onClickListener)
		}

		_onClick(event) {
			const button = event.target.closest('.gv-bundle-card__button')
			if (!button || button.disabled || !button.hasAttribute('data-variant-id')) {
				return
			}
			this._addToCart(button)
		}

		async _addToCart(button) {
			const variantId = button.getAttribute('data-variant-id')
			const quantity = parseInt(button.getAttribute('data-quantity') || '1', 10)
			const bundleHandle = button.getAttribute('data-bundle-handle')

			button.setAttribute('aria-busy', 'true')
			button.disabled = true

			const sectionsToBundle = ['variant-added', 'cart-drawer', 'cart-icon-bubble']
			document.documentElement.dispatchEvent(
				new CustomEvent('cart:prepare-bundled-sections', {
					bubbles: true,
					detail: { sections: sectionsToBundle },
				}),
			)

			try {
				const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Requested-With': 'XMLHttpRequest',
					},
					body: JSON.stringify({
						items: [
							{
								id: variantId,
								quantity,
								properties: bundleHandle
									? { _bundle: bundleHandle }
									: undefined,
							},
						],
						sections: sectionsToBundle,
					}),
				})

				const responseJson = await response.json()

				if (!response.ok) {
					this.dispatchEvent(
						new CustomEvent('cart:error', {
							bubbles: true,
							detail: { error: responseJson['description'] },
						}),
					)
					return
				}

				if (
					window.themeVariables?.settings?.cartType === 'page' ||
					window.themeVariables?.settings?.pageType === 'cart'
				) {
					window.location.href = `${Shopify.routes.root}cart`
					return
				}

				const cartResponse = await fetch(`${Shopify.routes.root}cart.js`)
				const cartContent = await cartResponse.json()
				cartContent['sections'] = responseJson['sections']

				this.dispatchEvent(
					new CustomEvent('variant:add', {
						bubbles: true,
						detail: {
							items: responseJson.hasOwnProperty('items')
								? responseJson['items']
								: [responseJson],
							cart: cartContent,
						},
					}),
				)
				document.documentElement.dispatchEvent(
					new CustomEvent('cart:change', {
						bubbles: true,
						detail: { baseEvent: 'bundle-engine:add', cart: cartContent },
					}),
				)
			} catch (error) {
				this.dispatchEvent(
					new CustomEvent('cart:error', {
						bubbles: true,
						detail: { error: error.message },
					}),
				)
			} finally {
				button.removeAttribute('aria-busy')
				button.disabled = false
			}
		}
	}

	window.customElements.define('bundle-engine', BundleEngine)
}
